import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Dialog,
  
  DialogContent,
  
  Button,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Chip,
  Select,
  IconButton,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Pets as PetsIcon,
  MyLocation as GpsIcon,
  Navigation as NavIcon,
  Close as CloseIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as PendingIcon,
  DirectionsCar as CarIcon,
  Home as HomeIcon,
  LocalHospital as ClinicIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  EditNote as EditIcon,
  Warning as WarningIcon,
  Place as PlaceIcon,
} from '@mui/icons-material';
import api from '../api/api';
import dayjs from 'dayjs';

// ─── Constantes ──────────────────────────────────
const ESTADOS_TRANSPORTE = [
  'PENDIENTE', 'CONFIRMADO', 'EN_CAMINO_RECOGIDA', 'RECOGIDO',
  'EN_CLINICA', 'SERVICIO_COMPLETADO', 'EN_CAMINO_ENTREGA',
  'ENTREGADO', 'CANCELADO', 'NO_ASISTIO'
];



const ESTADO_CONFIG = {
  'PENDIENTE':           { color: '#94a3b8', bg: '#f1f5f9', icon: PendingIcon,  label: 'Pendiente' },
  'CONFIRMADO':          { color: '#3b82f6', bg: '#eff6ff', icon: CheckIcon,    label: 'Confirmado' },
  'EN_CAMINO_RECOGIDA':  { color: '#f59e0b', bg: '#fffbeb', icon: CarIcon,      label: 'En camino a recoger' },
  'RECOGIDO':            { color: '#10b981', bg: '#ecfdf5', icon: CheckIcon,    label: 'Recogido' },
  'EN_CLINICA':          { color: '#8b5cf6', bg: '#f5f3ff', icon: ClinicIcon,   label: 'En clínica' },
  'SERVICIO_COMPLETADO': { color: '#059669', bg: '#ecfdf5', icon: CheckIcon,    label: 'Servicio listo' },
  'EN_CAMINO_ENTREGA':   { color: '#f97316', bg: '#fff7ed', icon: HomeIcon,     label: 'En camino a entregar' },
  'ENTREGADO':           { color: '#16a34a', bg: '#f0fdf4', icon: CheckIcon,    label: 'Entregado' },
  'CANCELADO':           { color: '#ef4444', bg: '#fef2f2', icon: CancelIcon,   label: 'Cancelado' },
  'NO_ASISTIO':          { color: '#9ca3af', bg: '#f9fafb', icon: CancelIcon,   label: 'No asistió' },
};

// ─── Selector de estado grande (para tarjetas) ────
const SelectorEstado = ({ value, onChange }) => {
  const cfg = ESTADO_CONFIG[value] || ESTADO_CONFIG['PENDIENTE'];
  

  return (
    <Select
      value={value || 'PENDIENTE'}
      onChange={(e) => onChange(e.target.value)}
      size="small"
      renderValue={(v) => {
        const c = ESTADO_CONFIG[v] || ESTADO_CONFIG['PENDIENTE'];
        const I = c.icon;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
            <I sx={{ fontSize: 18, color: c.color }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: c.color }}>{c.label}</Typography>
          </Box>
        );
      }}
      sx={{
        bgcolor: cfg.bg,
        border: `2px solid ${cfg.color}`,
        borderRadius: 2,
        minWidth: 180,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
        '& .MuiSelect-select': { py: 0.8, px: 1.2 },
      }}
    >
      {ESTADOS_TRANSPORTE.map((est) => {
        const c = ESTADO_CONFIG[est] || {};
        const I = c.icon || PendingIcon;
        return (
          <MenuItem key={est} value={est} sx={{ gap: 1, py: 1.2 }}>
            <I sx={{ fontSize: 20, color: c.color }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600, color: c.color }}>{c.label}</Typography>
          </MenuItem>
        );
      })}
    </Select>
  );
};

// ─── Tarjeta de parada ────────────────────────────
const TarjetaParada = ({ d, subzonaNombreMap, onOpenEdit, onUpdateStatus }) => {
  const estado = d.estado_transporte || 'PENDIENTE';
  const cfg = ESTADO_CONFIG[estado] || ESTADO_CONFIG['PENDIENTE'];
  const completado = ['ENTREGADO', 'SERVICIO_COMPLETADO', 'CANCELADO', 'NO_ASISTIO'].includes(estado);
  const paciente = d.paciente?.nombre_paciente || '—';
  const cliente = d.paciente?.cliente?.nombre_cliente || '—';
  const subzona = subzonaNombreMap[d.paciente?.cliente?.id_sub_zona] || '';

  return (
    <Box
      sx={{
        bgcolor: completado ? '#f8fafc' : 'white',
        borderRadius: 3,
        border: `2px solid ${completado ? '#e2e8f0' : cfg.color}`,
        mb: 2,
        overflow: 'hidden',
        boxShadow: completado ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
        opacity: completado ? 0.7 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      {/* Header de la tarjeta */}
      <Box
        sx={{
          bgcolor: completado ? '#f1f5f9' : cfg.bg,
          px: 2,
          py: 1.2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${cfg.color}22`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              bgcolor: cfg.color,
              color: 'white',
              borderRadius: 1,
              px: 1,
              py: 0.2,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.5,
            }}
          >
            #{d.id_logistica}
          </Box>
          {subzona && (
            <Chip
              icon={<PlaceIcon sx={{ fontSize: '14px !important' }} />}
              label={subzona}
              size="small"
              sx={{
                fontSize: 11,
                fontWeight: 700,
                bgcolor: '#e0f2fe',
                color: '#0369a1',
                height: 22,
                border: '1px solid #7dd3fc',
                '& .MuiChip-icon': { color: '#0369a1' },
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {d.hora_recogida_aprox && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, color: '#64748b', fontSize: 12 }}>
              <ScheduleIcon sx={{ fontSize: 14 }} />
              <span style={{ fontWeight: 700 }}>{dayjs(`2000-01-01 ${d.hora_recogida_aprox}`).format('hh:mm A')}</span>
            </Box>
          )}
        </Box>
      </Box>

      {/* Cuerpo */}
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        {/* Paciente y cliente */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.2 }}>
          <Box
            sx={{
              bgcolor: '#fef3c7',
              borderRadius: '50%',
              p: 0.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              mt: 0.2,
            }}
          >
            <PetsIcon sx={{ fontSize: 20, color: '#d97706' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 16, color: '#1e293b', lineHeight: 1.2 }}>
              {paciente}
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#64748b', fontWeight: 500, mt: 0.3 }}>
              {cliente}
            </Typography>
            <Typography variant="body2">
                📍 {d.paciente?.cliente?.direccion_cliente || 'Dirección no disponible'}
            </Typography>
          </Box>
        </Box>

        {/* Alerta de instrucción especial */}
        {d.notas_conductor && (
          <Box
            sx={{
              bgcolor: '#fff7ed',
              border: '2px solid #fed7aa',
              borderRadius: 2,
              px: 1.5,
              py: 1,
              mb: 1.2,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <WarningIcon sx={{ fontSize: 18, color: '#f97316', flexShrink: 0, mt: 0.2 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#c2410c' }}>
              {d.notas_conductor.toUpperCase()}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Footer con selector de estado y botón detalles */}
      <Box
        sx={{
          px: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <SelectorEstado value={estado} onChange={(v) => onUpdateStatus(d.id_logistica, v)} />
        <Button
          variant="contained"
          size="medium"
          onClick={() => onOpenEdit(d)}
          startIcon={<EditIcon />}
          sx={{
            bgcolor: '#1e293b',
            color: 'white',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 13,
            px: 2,
            '&:hover': { bgcolor: '#334155' },
          }}
        >
          Ver detalles
        </Button>
      </Box>
    </Box>
  );
};

// ─── Modal de detalles ────────────────────────────
const ModalDetalles = ({ open, onClose, domicilio, pacienteNombre, clienteNombre, subzona, latDefault, lngDefault, onSave }) => {
  const [editData, setEditData] = useState({ notas_clinica: '', hora_recogida_aprox: '', hora_entrega_aprox: '', latitud: null, longitud: null });
  const [obteniendoGps, setObteniendoGps] = useState(false);
  const [gpsOk, setGpsOk] = useState(false);

  useEffect(() => {
    if (domicilio && open) {
      setGpsOk(false);
      setEditData({
        notas_clinica: domicilio.notas_clinica || '',
        hora_recogida_aprox: domicilio.hora_recogida_aprox ? domicilio.hora_recogida_aprox.substring(0, 5) : '',
        hora_entrega_aprox: domicilio.hora_entrega_aprox ? domicilio.hora_entrega_aprox.substring(0, 5) : '',
        latitud: latDefault || null,
        longitud: lngDefault || null,
      });
    }
  }, [domicilio, open, latDefault, lngDefault]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const capturarGps = () => {
    if (!navigator.geolocation) { alert("Tu navegador no soporta geolocalización"); return; }
    setObteniendoGps(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setEditData(prev => ({ ...prev, latitud: pos.coords.latitude, longitud: pos.coords.longitude }));
        setObteniendoGps(false);
        setGpsOk(true);
      },
      (error) => { alert("Error al obtener GPS: " + error.message); setObteniendoGps(false); },
      { enableHighAccuracy: true }
    );
  };

  const abrirWaze = () => {
    const { latitud, longitud } = editData;
    if (latitud && longitud) {
      window.open(`https://www.waze.com/ul?ll=${latitud},${longitud}&navigate=yes`, '_blank');
    } else {
      alert("Este cliente aún no tiene coordenadas guardadas.");
    }
  };

  if (!domicilio) return null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      {/* Header */}
      <Box
        sx={{
          bgcolor: '#1e293b',
          color: 'white',
          px: 2,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography sx={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase' }}>
            Parada #{domicilio.id_logistica}
          </Typography>
          <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
            {pacienteNombre}
          </Typography>
          <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>{clienteNombre}</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white', bgcolor: '#334155', borderRadius: 2 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ bgcolor: '#f8fafc', p: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Zona */}
          {subzona && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e0f2fe', borderRadius: 2, px: 2, py: 1.2, border: '1px solid #7dd3fc' }}>
              <PlaceIcon sx={{ color: '#0369a1', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: '#0369a1', fontSize: 14 }}>Zona: {subzona.toUpperCase()}</Typography>
            </Box>
          )}

          {/* Instrucción especial */}
          {domicilio.notas_conductor && (
            <Box sx={{ bgcolor: '#fff7ed', border: '2px solid #fed7aa', borderRadius: 2, px: 2, py: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 0.5 }}>
                <WarningIcon sx={{ fontSize: 20, color: '#f97316' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#9a3412', textTransform: 'uppercase', letterSpacing: 0.5 }}>Instrucción especial</Typography>
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: 16, color: '#c2410c' }}>{domicilio.notas_conductor.toUpperCase()}</Typography>
            </Box>
          )}

          {/* Instrucciones del servicio */}
          {domicilio.hoja_servicio && (
            <Box sx={{ bgcolor: 'white', borderRadius: 2, px: 2, py: 1.5, border: '1px solid #e2e8f0' }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, mb: 0.5, textTransform: 'uppercase' }}>
                Instrucciones del servicio
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#475569' }}>{domicilio.hoja_servicio}</Typography>
            </Box>
          )}

          {/* Sección Navegación */}
          <Box sx={{ bgcolor: 'white', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <Box sx={{ bgcolor: '#0f172a', px: 2, py: 1 }}>
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>🗺️ NAVEGACIÓN</Typography>
            </Box>
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={abrirWaze}
                disabled={!editData.latitud}
                startIcon={<NavIcon />}
                sx={{
                  bgcolor: '#00d8ff',
                  color: '#001122',
                  fontWeight: 800,
                  fontSize: 16,
                  py: 1.8,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#00c4e8' },
                  '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                }}
              >
                Abrir en Waze
              </Button>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={capturarGps}
                disabled={obteniendoGps}
                startIcon={obteniendoGps ? <CircularProgress size={20} color="inherit" /> : <GpsIcon />}
                sx={{
                  borderColor: gpsOk ? '#10b981' : '#cbd5e1',
                  color: gpsOk ? '#059669' : '#475569',
                  fontWeight: 700,
                  fontSize: 15,
                  py: 1.5,
                  borderRadius: 2,
                  textTransform: 'none',
                  borderWidth: 2,
                  bgcolor: gpsOk ? '#ecfdf5' : 'transparent',
                }}
              >
                {obteniendoGps ? 'Obteniendo ubicación...' : gpsOk ? '✓ Ubicación guardada' : editData.latitud ? 'Actualizar mi ubicación' : 'Guardar mi ubicación aquí'}
              </Button>
            </Box>
          </Box>

          {/* Horarios */}
          <Box sx={{ bgcolor: 'white', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <Box sx={{ bgcolor: '#0f172a', px: 2, py: 1 }}>
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>⏱️ HORARIOS REALES</Typography>
            </Box>
            <Box sx={{ p: 2, display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.5, mb: 0.8, textTransform: 'uppercase' }}>
                  Hora de recogida
                </Typography>
                <TextField
                  type="time"
                  name="hora_recogida_aprox"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editData.hora_recogida_aprox}
                  onChange={handleChange}
                  inputProps={{ style: { fontSize: 22, fontWeight: 700, textAlign: 'center', padding: '12px 8px' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', letterSpacing: 0.5, mb: 0.8, textTransform: 'uppercase' }}>
                  Hora de entrega
                </Typography>
                <TextField
                  type="time"
                  name="hora_entrega_aprox"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={editData.hora_entrega_aprox}
                  onChange={handleChange}
                  inputProps={{ style: { fontSize: 22, fontWeight: 700, textAlign: 'center', padding: '12px 8px' } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Box>
            </Box>
          </Box>

          {/* Notas clínica */}
          <Box sx={{ bgcolor: 'white', borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <Box sx={{ bgcolor: '#0f172a', px: 2, py: 1 }}>
              <Typography sx={{ color: 'white', fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>📋 NOTAS PARA LA CLÍNICA</Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <TextField
                name="notas_clinica"
                fullWidth
                multiline
                rows={3}
                placeholder="Escribe aquí cualquier observación importante..."
                value={editData.notas_clinica}
                onChange={handleChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 15 } }}
              />
            </Box>
          </Box>

        </Box>
      </DialogContent>

      {/* Acciones */}
      <Box
        sx={{
          p: 2,
          bgcolor: 'white',
          borderTop: '1px solid #e2e8f0',
          display: 'flex',
          gap: 1.5,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          size="large"
          sx={{ flex: 1, fontWeight: 700, borderRadius: 2, textTransform: 'none', fontSize: 16, py: 1.5, borderColor: '#cbd5e1', color: '#64748b' }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={() => onSave(editData)}
          sx={{
            flex: 2,
            bgcolor: '#16a34a',
            fontWeight: 800,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: 17,
            py: 1.5,
            '&:hover': { bgcolor: '#15803d' },
          }}
        >
          ✓ Guardar
        </Button>
      </Box>
    </Dialog>
  );
};

// ─── Componente principal ─────────────────────────
const RutaProgramada = () => {
  const [domicilios, setDomicilios] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [subzonas, setSubzonas] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sucursalFiltro, setSucursalFiltro] = useState('');
  //const [sucursalUsuario, setSucursalUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDom, setSelectedDom] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  //useEffect(() => {
    //const token = localStorage.getItem('token');
    //if (token) {
      //try {
        //const payload = JSON.parse(atob(token.split('.')[1]));
        //const sucursalId = payload.id_sucursal || payload.sucursal_id || null;
        //if (sucursalId) { setSucursalUsuario(String(sucursalId)); setSucursalFiltro(String(sucursalId)); }
      //} catch (err) { console.error('Error token:', err); }
    //}
  //}, []);

  const subzonaNombreMap = useMemo(() =>
    subzonas.reduce((acc, sz) => { acc[sz.id_sub_zona] = sz.nombre_sub_zona; return acc; }, {}), [subzonas]);

  const fetchDatosRuta = useCallback(async () => {
  setLoading(true);
  try {
    const hoyStr = dayjs().format('YYYY-MM-DD');
    
    let params = `?fecha=${hoyStr}`;
    if (sucursalFiltro) {
      params += `&id_sucursal=${sucursalFiltro}`;
    }

    const [resLog, resSuc, resSub] = await Promise.all([
      api.get("/logistica_domicilio/" + params),
      api.get('/sucursales/'),
      api.get('/sub_zonas/'),
    ]);

    setSucursales(resSuc.data);
    setSubzonas(resSub.data);
    setDomicilios(resLog.data);
  } catch (err) {
    console.error(err);
    setSnackbar({ open: true, message: 'Error al cargar los datos', severity: 'error' });
  } finally { 
    setLoading(false); 
  }
}, [sucursalFiltro]);

// Cargar datos al montar el componente
useEffect(() => {
  fetchDatosRuta();
}, [fetchDatosRuta]);     // ← Cambiado

// Recargar cuando cambie el filtro de sucursal
useEffect(() => {
  if (sucursalFiltro !== undefined) {
    fetchDatosRuta();
  }
}, [sucursalFiltro, fetchDatosRuta]);   // ← Cambiado

  const handleSaveEdit = async (data) => {
    if (!selectedDom) return;
    try {
      await api.put(`/logistica_domicilio/${selectedDom.id_logistica}`, {
        ...selectedDom,
        notas_clinica: data.notas_clinica,
        hora_recogida_aprox: data.hora_recogida_aprox || null,
        hora_entrega_aprox: data.hora_entrega_aprox || null,
      });
      if (data.latitud && data.longitud) {
        await api.put(`/clientes/${selectedDom.id_cliente}`, {
          id_sucursal: selectedDom.id_sucursal,
          nombre_cliente: selectedDom.paciente?.cliente?.nombre_cliente,
          latitud: parseFloat(data.latitud),
          longitud: parseFloat(data.longitud),
        });
      }
      setSnackbar({ open: true, message: '✓ Guardado exitosamente', severity: 'success' });
      handleCloseModal();
      await fetchDatosRuta();
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al actualizar', severity: 'error' });
    }
  };

  const handleUpdateStatus = async (id_logistica, nuevoEstado) => {
    try {
      const registroActual = domicilios.find(d => d.id_logistica === id_logistica);
      const { paciente, ...payload } = { ...registroActual, estado_transporte: nuevoEstado };
      await api.put(`/logistica_domicilio/${id_logistica}`, payload);
      setDomicilios(prev => prev.map(d => d.id_logistica === id_logistica ? { ...d, estado_transporte: nuevoEstado } : d));
    } catch (err) {
      setSnackbar({ open: true, message: 'Error al cambiar estado', severity: 'error' });
    }
  };

  const domiciliosFiltrados = useMemo(() => {
    let result = domicilios;
    if (sucursalFiltro) result = result.filter((d) => String(d.id_sucursal) === String(sucursalFiltro));
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((d) => {
        const cliente = (d.paciente?.cliente?.nombre_cliente || '').toLowerCase();
        const paciente = (d.paciente?.nombre_paciente || '').toLowerCase();
        const subzona = (subzonaNombreMap[d.paciente?.cliente?.id_sub_zona] || '').toLowerCase();
        return cliente.includes(term) || paciente.includes(term) || subzona.includes(term);
      });
    }
    return result;
  }, [domicilios, searchTerm, sucursalFiltro, subzonaNombreMap]);

  // Contadores de resumen
  const totalEntregados = domicilios.filter(d => d.estado_transporte === 'ENTREGADO').length;
  const totalPendientes = domicilios.filter(d => !['ENTREGADO', 'CANCELADO', 'NO_ASISTIO', 'SERVICIO_COMPLETADO'].includes(d.estado_transporte)).length;

  const handleOpenEdit = (dom) => { setSelectedDom(dom); setOpenModal(true); };
  const handleCloseModal = () => { setOpenModal(false); setSelectedDom(null); };

  // ─── Pantalla de carga ────
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
        <CircularProgress size={56} sx={{ color: '#1e293b' }} />
        <Typography sx={{ color: '#64748b', fontWeight: 600, fontSize: 16 }}>Cargando ruta del día...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9' }}>

      {/* Header */}
     {/*  <Box sx={{ bgcolor: '#1e293b', px: 2, pt: 3, pb: 2, position: 'sticky', top: 0, zIndex: 100 }}>  */}
     <Box sx={{ bgcolor: '#1e293b', px: 2, pt: 3, pb: 2 }}>

  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
    <Box>
      <Typography sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
        {dayjs().format('dddd DD [de] MMMM')}
      </Typography>
      <Typography sx={{ color: 'white', fontSize: 20, fontWeight: 900, lineHeight: 1.2 }}>
        🚗 Ruta del día
      </Typography>
    </Box>
    <IconButton onClick={fetchDatosRuta} sx={{ color: 'white', bgcolor: '#334155', borderRadius: 2, p: 1 }}>
      <RefreshIcon />
    </IconButton>
  </Box>

  {/* ←←← SELECTOR DE SUCURSAL ←←← */}
  <Box sx={{ mb: 2 }}>
    <Typography sx={{ color: '#94a3b8', fontSize: 12, mb: 0.8 }}>Sucursal</Typography>
    <Select
      fullWidth
      value={sucursalFiltro || ''}
      onChange={(e) => setSucursalFiltro(e.target.value)}
      sx={{
        bgcolor: 'white',
        borderRadius: 2,
        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
      }}
    >
      <MenuItem value="">Todas las sucursales</MenuItem>
      {sucursales.map((suc) => (
        <MenuItem key={suc.id_sucursal} value={String(suc.id_sucursal)}>
          {suc.nombre_sucursal}
        </MenuItem>
      ))}
    </Select>
  </Box>

  {/* Resumen rápido */}
  <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
    <Box sx={{ flex: 1, bgcolor: '#0f172a', borderRadius: 2, p: 1.2, textAlign: 'center', border: '1px solid #334155' }}>
      <Typography sx={{ color: '#f59e0b', fontSize: 22, fontWeight: 900 }}>{totalPendientes}</Typography>
      <Typography sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Pendientes</Typography>
    </Box>
    <Box sx={{ flex: 1, bgcolor: '#0f172a', borderRadius: 2, p: 1.2, textAlign: 'center', border: '1px solid #334155' }}>
      <Typography sx={{ color: '#10b981', fontSize: 22, fontWeight: 900 }}>{totalEntregados}</Typography>
      <Typography sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Entregados</Typography>
    </Box>
    <Box sx={{ flex: 1, bgcolor: '#0f172a', borderRadius: 2, p: 1.2, textAlign: 'center', border: '1px solid #334155' }}>
      <Typography sx={{ color: 'white', fontSize: 22, fontWeight: 900 }}>{domicilios.length}</Typography>
      <Typography sx={{ color: '#94a3b8', fontSize: 11, fontWeight: 600 }}>Total</Typography>
    </Box>
  </Box>

  {/* Búsqueda */}
  <TextField
    fullWidth
    placeholder="Buscar mascota, cliente o zona..."
    value={searchTerm}
    onChange={(e) => setSearchTerm(e.target.value)}
    InputProps={{
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon sx={{ color: '#64748b' }} />
        </InputAdornment>
      ),
    }}
    sx={{
      '& .MuiOutlinedInput-root': {
        bgcolor: 'white',
        borderRadius: 2,
        fontSize: 15,
        '& fieldset': { border: 'none' },
      },
    }}
  />
</Box>

      {/* Lista de paradas */}
      <Box sx={{ p: 2 }}>
        {domiciliosFiltrados.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography sx={{ fontSize: 48 }}>🐾</Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: 16, fontWeight: 600, mt: 1 }}>
              {searchTerm ? 'No hay resultados para esa búsqueda' : 'No hay paradas programadas hoy'}
            </Typography>
          </Box>
        ) : (
          domiciliosFiltrados.map((d) => (
            <TarjetaParada
              key={d.id_logistica}
              d={d}
              subzonaNombreMap={subzonaNombreMap}
              onOpenEdit={handleOpenEdit}
              onUpdateStatus={handleUpdateStatus}
            />
          ))
        )}
      </Box>

      {/* Modal */}
      <ModalDetalles
        open={openModal}
        onClose={handleCloseModal}
        domicilio={selectedDom}
        pacienteNombre={selectedDom?.paciente?.nombre_paciente || ''}
        clienteNombre={selectedDom?.paciente?.cliente?.nombre_cliente || ''}
        subzona={subzonaNombreMap[selectedDom?.paciente?.cliente?.id_sub_zona] || ''}
        latDefault={selectedDom?.paciente?.cliente?.latitud}
        lngDefault={selectedDom?.paciente?.cliente?.longitud}
        onSave={handleSaveEdit}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ fontSize: 15, fontWeight: 700, borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RutaProgramada;